import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getToken, setToken, clearToken } from "@/lib/api";
import { setActiveCurrency, DEFAULT_CURRENCY } from "@/lib/currency";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [entitlements, setEntitlements] = useState({});
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.data.user);
      setPermissions(data.data.permissions || []);
      setEntitlements(data.data.entitlements || {});
      const cur = data.data.currency || DEFAULT_CURRENCY;
      setCurrency(cur);
      setActiveCurrency(cur);
    } catch {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password, { mfaCode, tenant, remember = true } = {}) => {
    const { data } = await api.post("/auth/login", { email, password, mfaCode, tenant });
    setToken(data.data.accessToken, remember);
    setUser(data.data.user);
    await loadMe();
    return data.data.user;
  };

  const refreshAuth = useCallback(() => loadMe(), [loadMe]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    clearToken();
    setUser(null);
    setPermissions([]);
    window.location.href = "/login";
  };

  const hasPermission = useCallback(
    (perm) => permissions.includes("*") || permissions.includes(perm),
    [permissions]
  );

  return (
    <AuthContext.Provider
      value={{ user, permissions, entitlements, currency, loading, login, logout, refreshAuth, hasPermission, isSuperAdmin: !!user?.isSuperAdmin }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
